<script setup lang="ts">
import type {
	DateRange,
	DateRangeParsed,
	DateRangePreset,
} from "@/ui/components/date/types";

import { dateRangePresets } from "@/ui/components/date/types";
import {
	isDateRangeValid,
	parseBound,
} from "@/ui/components/date/dateRangeValidation";
import { computed, nextTick, ref, watch } from "vue";
import { isValid } from "chartjs-graph";
import dayjs from "dayjs";
import DynamicDatesHelpDialog from "@/modules/explorer/components/DynamicDatesHelpDialog.vue";

interface ComponentProps {
	presets?: DateRangePreset[];
	showPresets: boolean;
	showFooter?: boolean;
	isRange?: boolean;
	showApply?: boolean;
	nullable?: boolean;
}

const props = withDefaults(defineProps<ComponentProps>(), {
	presets: () => dateRangePresets,
	showPresets: true,
	showFooter: true,
	isRange: false,
	showApply: true,
	nullable: false,
});

const emit = defineEmits<{
	(e: "apply-dates"): void;
	(e: "close"): void;
	(e: "date-validation", value: boolean): void;
}>();

const filter = ref("");
const isToOpen = ref(false);
const isFromOpen = ref(false);
const showHelp = ref(false);
// Add a flag to track when we're in the middle of an update
const isUpdating = ref(false);

const model = defineModel<DateRange | undefined>();
const parsed = defineModel<DateRangeParsed | undefined>("parsed");

const filteredPresets = computed(() => {
	return props.presets.filter((preset) =>
		preset.display.toLowerCase().includes(filter.value.toLowerCase())
	);
});

const calendarValue = computed(() => {
	return {
		start: parsed.value?.from?.toDate() ?? undefined,
		end: parsed.value?.to?.toDate() ?? undefined,
	};
});

const isValidValue = computed(() =>
	isDateRangeValid(model.value, parsed.value, props.nullable)
);

const emptyBound = () => (props.nullable ? null : undefined);

watch(
	isValidValue,
	(newVal) => {
		emit("date-validation", newVal);
	},
	{
		immediate: true,
	}
);

const slotProps = computed(() => {
	return {
		disabled: !isValidValue.value,
		model: model.value,
		parsed: parsed.value,
	};
});

async function updateDate(
	val: any,
	// cal -> calender
	variant:
		| "calFrom"
		| "calTo"
		| "calRange"
		| "preset"
		| "manualTo"
		| "manualFrom"
		| "clearFrom"
		| "clearTo"
) {
	// double-clicking the calendar makes it think you want to deselect the date, so it returns null
	if (val === null && (variant === "calFrom" || variant === "calTo")) {
		// manually close calendar
		if (variant === "calTo") {
			isToOpen.value = false;
		} else {
			isFromOpen.value = false;
		}
		return;
	}
	// If we're already in the process of updating, don't trigger another update
	if (isUpdating.value) return;

	// Set the flag to indicate we're updating
	isUpdating.value = true;

	isToOpen.value = isFromOpen.value = false;

	switch (variant) {
		case "calFrom": {
			model.value.from = dayjs(val as Date)
				.startOf("day")
				.format("YYYY-MM-DD HH:mm:ss");
			break;
		}
		case "calTo": {
			model.value.to = dayjs(val as Date)
				.endOf("day")
				.format("YYYY-MM-DD HH:mm:ss");
			break;
		}
		case "manualTo": {
			model.value.to = val === "" ? emptyBound() : val;
			break;
		}
		case "manualFrom": {
			model.value.from = val === "" ? emptyBound() : val;
			break;
		}
		case "preset": {
			const preset = val as DateRangePreset;
			model.value.from = preset.from;
			model.value.to = preset.to;
			break;
		}
		case "clearFrom": {
			model.value.from = null;
			break;
		}
		case "clearTo": {
			model.value.to = null;
			break;
		}
		default:
			throw new Error("Invalid date range variant, not implemented!");
	}

	// Keep parsed in the same three states as the model, so clearing one field
	// doesn't destroy the unbounded sentinel on the other
	parsed.value.from = parseBound(model.value.from);
	parsed.value.to = parseBound(model.value.to, true);

	// Use nextTick to wait for Vue to finish rendering before resetting the flag
	// This ensures all reactive effects have completed before we allow new updates
	await nextTick(() => {
		isUpdating.value = false;
	});
	if (!props.showApply) {
		// if this component has no apply button, we apply the dates on each update.
		emit("apply-dates");
		// if the user selects a "to" we close the menu
		if (variant === "calTo") {
			emit("close");
		}
	}
}

const apply = () => {
	emit("apply-dates");
	emit("close");
};
</script>

<template>
	<dynamic-dates-help-dialog v-model="showHelp" />
	<div class="date-picker" :data-presets="showPresets">
		<div id="left">
			<div class="d-flex ga-2">
				<v-menu
					v-model="isFromOpen"
					:close-on-content-click="false"
					location="bottom"
				>
					<template v-slot:activator="{ props }">
						<v-text-field
							:model-value="model?.from"
							:persistent-hint="model?.from?.includes('now')"
							:hint="
								model?.from?.includes('now')
									? parsed?.from
										? parsed.from.format('YYYY-MM-DD HH:mm:ss')
										: ''
									: undefined
							"
							variant="outlined"
							density="compact"
							hide-details="auto"
							v-bind="props"
							label="From"
							@update:modelValue="(v) => updateDate(v, 'manualFrom')"
							append-inner-icon="mdi-calendar"
							:error="model?.from && parsed?.from && !isValid(parsed.from)"
							:error-messages="
								model?.from && parsed?.from && !isValid(parsed.from)
									? 'Invalid date format'
									: undefined
							"
						>
						</v-text-field>
					</template>
					<vc-date-picker
						@dayclick="
							(_, event) => {
								event.target.blur();
							}
						"
						class="elevation-4 mt-2"
						show-iso-weeknumbers
						:first-day-of-week="2"
						:rules="[]"
						:model-value="calendarValue.start"
						@update:modelValue="(v) => updateDate(v, 'calFrom')"
						:attributes="[{ key: 'today', dot: true, dates: new Date() }]"
					>
					</vc-date-picker>
				</v-menu>
				<v-btn
					v-if="props.nullable"
					color="error"
					variant="tonal"
					width="40"
					height="40"
					rounded
					icon="mdi-delete"
					:disabled="!model?.from"
					@click="() => updateDate(null, 'clearFrom')"
				/>
			</div>
			<div class="d-flex ga-2 mt-4">
				<v-menu
					v-model="isToOpen"
					:close-on-content-click="false"
					location="bottom"
				>
					<template v-slot:activator="{ props }">
						<v-text-field
							:model-value="model?.to"
							:persistent-hint="model?.to?.includes('now')"
							:hint="
								model?.to?.includes('now')
									? parsed?.to
										? parsed.to.format('YYYY-MM-DD HH:mm:ss')
										: ''
									: undefined
							"
							variant="outlined"
							density="compact"
							label="To"
							hide-details="auto"
							v-bind="props"
							@update:modelValue="(v) => updateDate(v, 'manualTo')"
							append-inner-icon="mdi-calendar"
							:error="model?.to && parsed?.to && !isValid(parsed.to)"
							:error-messages="
								model?.to && parsed?.to && !isValid(parsed.to)
									? 'Invalid date format'
									: undefined
							"
						>
						</v-text-field>
					</template>
					<vc-date-picker
						@dayclick="
							(_, event) => {
								event.target.blur();
							}
						"
						class="elevation-4 mt-2"
						show-iso-weeknumbers
						:first-day-of-week="2"
						:rules="[]"
						:model-value="calendarValue.end"
						@update:modelValue="(v) => updateDate(v, 'calTo')"
						:attributes="[{ key: 'today', dot: true, dates: new Date() }]"
					>
					</vc-date-picker>
				</v-menu>
				<v-btn
					v-if="props.nullable"
					color="error"
					variant="tonal"
					width="40"
					height="40"
					rounded
					icon="mdi-delete"
					:disabled="!model?.to"
					@click="() => updateDate(null, 'clearTo')"
				/>
			</div>
			<template v-if="props.showApply">
				<slot name="aboveSubmitButton"></slot>

				<v-divider class="my-2" />
				<slot name="button" v-bind="slotProps">
					<v-btn
						:disabled="!isValidValue"
						block
						variant="flat"
						color="secondary"
						prepend-icon="mdi-calendar"
						@click="apply"
					>
						Apply
					</v-btn>
				</slot>
			</template>
		</div>
		<template v-if="showPresets">
			<v-divider vertical />
			<div id="right">
				<div class="search">
					<v-text-field
						placeholder="Search presets"
						rounded="0"
						variant="filled"
						class="mb-2"
						hide-details="auto"
						v-model="filter"
						prepend-inner-icon="mdi-magnify"
					>
					</v-text-field>
				</div>
				<v-list density="compact" nav class="pt-0">
					<v-list-item
						v-for="preset in filteredPresets"
						:key="preset.display"
						@click="updateDate(preset, 'preset')"
						rounded
						:active="preset.from === model?.from && preset.to === model?.to"
						v-memo="[preset.from === model?.from && preset.to === model?.to]"
						:min-height="30"
					>
						<v-list-item-title
							class="d-flex align-center text-body-2"
							style="min-height: 22px"
						>
							{{ preset.display }}
						</v-list-item-title>
					</v-list-item>
				</v-list>
			</div>
		</template>
	</div>

	<div class="d-flex align-center ga-2 pa-2 border-t">
		<v-chip
			color="secondary"
			density="comfortable"
			variant="flat"
			class="text-caption"
			size="small"
		>
			Netherlands, CEST
		</v-chip>
		<span class="text-caption text-medium-emphasis">Europe/Amsterdam</span>
		<v-spacer />
		<v-icon
			@click="showHelp = true"
			color="on-surface"
			class="cursor-pointer text-medium-emphasis"
		>
			mdi-help-circle-outline
		</v-icon>
	</div>
</template>

<style scoped>
.date-picker {
	display: grid;
	grid-template-columns: 1.1fr auto 1fr;

	&[data-presets="false"] {
		display: block;
	}

	#right {
		& .search {
			background: white;
			position: sticky;
			top: 0;
			z-index: 1;
		}
	}

	#left {
		padding: 1rem;
	}

	#right {
		height: 400px;
		overflow-y: auto;
	}
}
</style>
